/**
 * @file useFormHelpers.js
 * @description Form helper functions and validation logic for BRICK Schema forms
 *
 * Handles:
 * - BRICK class filtering for equipment/devices/points
 * - Form default value generation
 * - Location picker logic and breadcrumb building
 * - Unit label formatting
 * - Quantity kind inference
 */

import { ref, watch } from 'vue';

/**
 * Device point type constraints mapping
 * Maps BRICK device classes to allowed point types
 */
const devicePointConstraints = {
  // Sensors - typically only inputs
  'brick:Temperature_Sensor': ['AI', 'Virtual'],
  'brick:Humidity_Sensor': ['AI', 'Virtual'],
  'brick:Pressure_Sensor': ['AI', 'Virtual'],
  'brick:CO2_Sensor': ['AI', 'Virtual'],
  'brick:CO_Sensor': ['AI', 'Virtual'],
  'brick:Air_Quality_Sensor': ['AI', 'Virtual'],
  'brick:Smoke_Detector': ['DI', 'Virtual'],
  'brick:Differential_Pressure_Sensor': ['AI', 'Virtual'],
  'brick:Occupancy_Sensor': ['DI', 'Virtual'],
  'brick:Flow_Sensor': ['AI', 'Virtual'],

  // Filters and Treatment
  'brick:Filter': ['DI', 'Virtual'], // Digital input for clogged status
  'brick:UV_Light': ['DO', 'DI', 'Virtual'], // On/off control and status

  // Coils - typically analog control with feedback
  'brick:Cooling_Coil': ['AO', 'AI', 'Virtual'],
  'brick:Heating_Coil': ['AO', 'AI', 'Virtual'],
  'brick:Hot_Water_Coil': ['AO', 'AI', 'Virtual'],

  // Dampers - analog or digital
  'brick:Damper': ['AO', 'DO', 'AI', 'DI', 'Virtual'],
  'brick:Return_Damper': ['AO', 'DO', 'AI', 'DI', 'Virtual'],
  'brick:Outside_Damper': ['AO', 'DO', 'AI', 'DI', 'Virtual'],
  'brick:Exhaust_Damper': ['AO', 'DO', 'AI', 'DI', 'Virtual'],
  'brick:Mixed_Damper': ['AO', 'DO', 'AI', 'DI', 'Virtual'],
  'brick:Economizer_Damper': ['AO', 'DO', 'AI', 'DI', 'Virtual'],
  'brick:Isolation_Damper': ['DO', 'DI', 'Virtual'],

  // Fans - typically outputs with status inputs
  'brick:Fan': ['AO', 'DO', 'AI', 'DI', 'Virtual'],
  'brick:Supply_Fan': ['AO', 'DO', 'AI', 'DI', 'Virtual'],
  'brick:Return_Fan': ['AO', 'DO', 'AI', 'DI', 'Virtual'],
  'brick:Exhaust_Fan': ['AO', 'DO', 'AI', 'DI', 'Virtual'],
  'brick:Relief_Fan': ['AO', 'DO', 'AI', 'DI', 'Virtual'],
  'brick:VFD': ['AO', 'AI', 'DO', 'DI', 'Virtual', 'HLI'], // VFD can have various point types

  // Valves - analog control with feedback
  'brick:Valve': ['AO', 'AI', 'Virtual'],
  'brick:Heating_Valve': ['AO', 'AI', 'Virtual'],
  'brick:Cooling_Valve': ['AO', 'AI', 'Virtual'],
  'brick:Chilled_Water_Valve': ['AO', 'AI', 'Virtual'],
  'brick:Hot_Water_Valve': ['AO', 'AI', 'Virtual'],
  'brick:Water_Valve': ['AO', 'AI', 'Virtual'],
  'brick:Isolation_Valve': ['DO', 'DI', 'Virtual'],

  // Air Handlers - all point types
  'brick:AHU': ['AI', 'AO', 'DI', 'DO', 'Virtual', 'HLI'],
  'brick:Air_Handler_Unit': ['AI', 'AO', 'DI', 'DO', 'Virtual', 'HLI'],
  'brick:RTU': ['AI', 'AO', 'DI', 'DO', 'Virtual', 'HLI'],
  'brick:Rooftop_Unit': ['AI', 'AO', 'DI', 'DO', 'Virtual', 'HLI'],
  'brick:FCU': ['AI', 'AO', 'DI', 'DO', 'Virtual', 'HLI'],
  'brick:Fan_Coil_Unit': ['AI', 'AO', 'DI', 'DO', 'Virtual', 'HLI'],
};

/**
 * Point type options for dropdowns
 */
const pointTypeOptions = [
  { label: 'Analog Input (AI)', value: 'AI' },
  { label: 'Analog Output (AO)', value: 'AO' },
  { label: 'Digital Input (DI)', value: 'DI' },
  { label: 'Digital Output (DO)', value: 'DO' },
  { label: 'Virtual', value: 'Virtual' },
  { label: 'High Level Interface (HLI)', value: 'HLI' },
];

/**
 * Form helpers composable
 * @param {Object} params - Composable parameters
 * @param {Ref} params.selectedEquipment - Currently selected equipment ref
 * @param {Ref} params.localHierarchyData - Local hierarchy data ref
 * @param {Ref} params.newDevice - New device form data ref
 * @param {Ref} params.editingDevice - Editing device form data ref
 * @param {Ref} params.showEditDeviceDialog - Show edit device dialog ref
 * @param {Ref} params.locationTreeNodes - Location tree nodes ref
 * @param {Ref} params.showLocationPicker - Show location picker dialog ref
 * @returns {Object} Form helper methods and state
 */
export function useFormHelpers(params = {}) {
  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Breadcrumb path for device location
   * @type {Ref<Array<Object>>}
   */
  const deviceLocationPath = ref([]);

  /**
   * Selected node in location picker tree
   * @type {Ref<string|null>}
   */
  const selectedLocationNode = ref(null);

  /**
   * Selected space ID in location picker
   * @type {Ref<number|null>}
   */
  const selectedLocationForPicker = ref(null);

  /**
   * Location picker mode: 'add', 'edit', or 'switchboard'
   * @type {Ref<string>}
   */
  const locationPickerMode = ref('add');

  // ============================================================================
  // BRICK CLASS FILTERING
  // ============================================================================

  /**
   * Filter BRICK classes (placeholder for future dynamic filtering)
   * @param {string} val - Filter value
   * @param {Function} update - Update callback for q-select
   */
  function filterBrickClasses(val, update) {
    update();
  }

  /**
   * Get allowed point types for a device BRICK class
   * @param {string} deviceBrickClass - Device BRICK class
   * @returns {Array<string>} Allowed point type values
   */
  function getAllowedPointTypes(deviceBrickClass) {
    return devicePointConstraints[deviceBrickClass] || ['AI', 'AO', 'DI', 'DO', 'Virtual', 'HLI'];
  }

  /**
   * Get filtered point type options based on selected equipment
   * @returns {Array<Object>} Filtered point type options
   */
  function getFilteredPointTypes() {
    if (params.selectedEquipment?.value?.brick_class) {
      const allowed = getAllowedPointTypes(params.selectedEquipment.value.brick_class);
      return pointTypeOptions.filter((opt) => allowed.includes(opt.value));
    }
    return pointTypeOptions;
  }

  // ============================================================================
  // FORM DEFAULTS
  // ============================================================================

  /**
   * Update level form defaults based on BRICK class
   * @param {Ref} newLevel - New level form data ref
   */
  function updateLevelDefaults(newLevel) {
    // Auto-generate level name based on BRICK class
    if (newLevel.value.brick_class === 'brick:Floor') {
      newLevel.value.name = `Level ${newLevel.value.level_number || '1'}`;
    } else if (newLevel.value.brick_class === 'brick:Basement') {
      newLevel.value.name = `Basement ${newLevel.value.level_number || '1'}`;
    } else if (newLevel.value.brick_class === 'brick:Rooftop') {
      newLevel.value.name = 'Rooftop';
    } else if (newLevel.value.brick_class === 'brick:Mezzanine') {
      newLevel.value.name = 'Mezzanine';
    } else if (newLevel.value.brick_class === 'brick:Penthouse') {
      newLevel.value.name = 'Penthouse';
    }
  }

  /**
   * Update space form defaults based on BRICK class
   * @param {Ref} newSpace - New space form data ref
   * @param {Function} formatBrickClass - Function to format BRICK class for display
   */
  function updateSpaceDefaults(newSpace, formatBrickClass) {
    // Auto-generate space name based on BRICK class
    if (!newSpace.value.name && newSpace.value.brick_class) {
      // Use the formatBrickClass helper to generate a readable name
      newSpace.value.name = formatBrickClass(newSpace.value.brick_class);
    }
  }

  /**
   * Infer quantity kind from BRICK class or point name
   * @param {string} brickClass - BRICK class
   * @returns {string|null} Inferred quantity kind or null
   */
  function inferQuantityKind(brickClass) {
    if (!brickClass) {return null;}
    const lower = brickClass.toLowerCase();

    if (lower.includes('temperature')) {return 'Temperature';}
    if (lower.includes('pressure')) {return 'Pressure';}
    if (lower.includes('flow') || lower.includes('airflow')) {return 'Flow';}
    if (lower.includes('power')) {return 'Power';}
    if (lower.includes('speed') || lower.includes('frequency')) {return 'Speed';}
    if (lower.includes('energy')) {return 'Energy';}
    if (lower.includes('humidity')) {return 'RelativeHumidity';}
    if (lower.includes('damper') || lower.includes('valve')) {return 'Dimensionless';}

    return null;
  }

  // ============================================================================
  // LOCATION PICKER LOGIC
  // ============================================================================

  /**
   * Build location breadcrumb path from space ID
   * @param {number} spaceId - Space ID
   */
  function buildLocationPath(spaceId) {
    const path = [];

    if (!spaceId || !params.localHierarchyData?.value?.buildings) {
      deviceLocationPath.value = [];
      return;
    }

    // Find the space and build path: Building > Level > Space
    if (!params.localHierarchyData?.value?.buildings) {return [];}

    for (const building of params.localHierarchyData.value.buildings) {
      if (building.levels) {
        for (const level of building.levels) {
          if (level.spaces) {
            const space = level.spaces.find((s) => s.id === spaceId);
            if (space) {
              // Build the path
              path.push({
                label: building.name,
                icon: 'business',
                id: building.id,
                type: 'building',
              });
              path.push({
                label: level.name,
                icon: 'layers',
                id: level.id,
                type: 'level',
              });
              path.push({
                label: space.name,
                icon: 'meeting_room',
                id: space.id,
                type: 'space',
              });
              deviceLocationPath.value = path;
              return;
            }
          }
        }
      }
    }

    deviceLocationPath.value = [];
  }

  /**
   * Initialize device location when opening dialogs
   * @param {number} spaceId - Space ID
   */
  function initializeDeviceLocation(spaceId) {
    if (spaceId) {
      buildLocationPath(spaceId);
      params.newDevice.value.location_space_id = spaceId;
    } else {
      deviceLocationPath.value = [];
    }
  }

  /**
   * Handle location selection in picker dialog
   * @param {number} spaceId - Selected space ID
   */
  function onLocationSelected(spaceId) {
    if (params.showEditDeviceDialog?.value) {
      params.editingDevice.value.location_space_id = spaceId;
    } else {
      params.newDevice.value.location_space_id = spaceId;
    }
    buildLocationPath(spaceId);
    if (params.showLocationPicker) {
      params.showLocationPicker.value = false;
    }
  }

  /**
   * Open location picker dialog
   * @param {string} mode - Picker mode: 'add', 'edit', or 'switchboard'
   */
  function openLocationPicker(mode) {
    locationPickerMode.value = mode;

    // Pre-select current location if exists
    if (mode === 'edit' && params.editingDevice?.value?.location_space_id) {
      selectedLocationNode.value = `space-${params.editingDevice.value.location_space_id}`;
      selectedLocationForPicker.value = params.editingDevice.value.location_space_id;
    } else if (mode === 'add' && params.newDevice?.value?.location_space_id) {
      selectedLocationNode.value = `space-${params.newDevice.value.location_space_id}`;
      selectedLocationForPicker.value = params.newDevice.value.location_space_id;
    } else {
      selectedLocationNode.value = null;
      selectedLocationForPicker.value = null;
    }

    if (params.showLocationPicker) {
      params.showLocationPicker.value = true;
    }
  }

  /**
   * Clear device location
   * @param {string} mode - 'edit' or 'add'
   */
  function clearDeviceLocation(mode) {
    if (mode === 'edit' && params.editingDevice) {
      params.editingDevice.value.location_space_id = null;
      params.editingDevice.value.location_description = '';
    } else if (params.newDevice) {
      params.newDevice.value.location_space_id = null;
      params.newDevice.value.location_description = '';
    }
    deviceLocationPath.value = [];
  }

  /**
   * Get selected location label for picker dialog
   * @returns {string} Location breadcrumb label
   */
  function getSelectedLocationLabel() {
    if (!selectedLocationNode.value) {return '';}

    const node = findNodeByKey(params.locationTreeNodes?.value || [], selectedLocationNode.value);
    if (!node) {return '';}

    const path = [];
    if (node.building) {path.push(node.building);}
    if (node.level) {path.push(node.level);}
    path.push(node.label);

    return path.join(' > ');
  }

  /**
   * Find node by key in tree
   * @param {Array<Object>} nodes - Tree nodes
   * @param {string} key - Node key to find
   * @returns {Object|null} Found node or null
   */
  function findNodeByKey(nodes, key) {
    for (const node of nodes) {
      if (node.key === key) {return node;}
      if (node.children) {
        const found = findNodeByKey(node.children, key);
        if (found) {return found;}
      }
    }
    return null;
  }

  /**
   * Confirm location selection in picker dialog
   */
  function confirmLocationSelection() {
    if (!selectedLocationNode.value) {return;}

    const node = findNodeByKey(params.locationTreeNodes?.value || [], selectedLocationNode.value);
    if (!node || !node.spaceId) {return;}

    // Update the appropriate device location
    if (locationPickerMode.value === 'edit' && params.editingDevice) {
      params.editingDevice.value.location_space_id = node.spaceId;
    } else if (params.newDevice) {
      params.newDevice.value.location_space_id = node.spaceId;
    }

    // Build and update the breadcrumb path
    buildLocationPath(node.spaceId);

    // Close the dialog
    if (params.showLocationPicker) {
      params.showLocationPicker.value = false;
    }
  }

  /**
   * Open location picker for switchboard
   */
  function openLocationPickerForSwitchboard() {
    locationPickerMode.value = 'switchboard';
    if (params.showLocationPicker) {
      params.showLocationPicker.value = true;
    }
  }

  // ============================================================================
  // UNIT HELPERS
  // ============================================================================

  /**
   * Get formatted unit label (placeholder - may be implemented later)
   * @param {string} unit - Unit value
   * @returns {string} Formatted unit label
   */
  function getUnitLabel(unit) {
    // Placeholder for future unit label formatting
    return unit || '';
  }

  // ============================================================================
  // WATCHERS
  // ============================================================================

  // Watch for location node selection in picker
  watch(selectedLocationNode, (newValue) => {
    if (!newValue) {
      selectedLocationForPicker.value = null;
      return;
    }

    const node = findNodeByKey(params.locationTreeNodes?.value || [], newValue);
    if (node && node.spaceId) {
      selectedLocationForPicker.value = node.spaceId;
    } else {
      selectedLocationForPicker.value = null;
    }
  });

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // State
    deviceLocationPath,
    selectedLocationNode,
    selectedLocationForPicker,
    locationPickerMode,
    pointTypeOptions,

    // BRICK class filtering
    filterBrickClasses,
    getAllowedPointTypes,
    getFilteredPointTypes,

    // Form defaults
    updateLevelDefaults,
    updateSpaceDefaults,
    inferQuantityKind,

    // Location picker
    buildLocationPath,
    initializeDeviceLocation,
    onLocationSelected,
    openLocationPicker,
    clearDeviceLocation,
    getSelectedLocationLabel,
    findNodeByKey,
    confirmLocationSelection,
    openLocationPickerForSwitchboard,

    // Unit helpers
    getUnitLabel,
  };
}
